import React, { useCallback, useMemo, useState } from 'react'
import { selectedFieldAssetsStore } from 'stores/selectedFieldAssetsStore/selectedFieldAssetsStore'
import { DEVICE_TYPES } from '../../Map/_utils/deviceTypes'
import { EQUIPMENT_TYPES } from '../../Map/_utils/equipmentTypes'
import { getActiveNodeStatus } from '../../Map/_utils/getActiveNodeStatus'
import { getNodeDeviceTypes } from '../../Map/_utils/getNodeDeviceTypes'
import { getNodeEquipmentType } from '../../Map/_utils/getNodeEquipmentType'
import { serviceCenterStore, type TEquipmentType, type TNodeDeviceType } from '../../store/serviceCenterStore'
import type { TActiveGateway, TActiveNode, TPlannedGateway, TPlannedNode } from '../../types'
import { TNodeStatus } from '../../types'
import { getIdentifier } from '../../utils/getIdentifier'
import { isPlannedNode } from '../../utils/isPlannedNode'
import { Filters } from './Filters/Filters'
import { NodeList } from './NodeList/NodeList'
import { ServiceIssueSummary } from './ServiceIssueSummary/ServiceIssueSummary'

export type NodeWithStatus = (TActiveNode | TActiveGateway | TPlannedNode | TPlannedGateway) & {
  status: TNodeStatus
}

export type TFilters = {
  searchKeywords: string
  equipmentTypes: TEquipmentType[]
  deviceTypes: TNodeDeviceType[]
  statuses: TNodeStatus[]
  insectIds: number[]
  issueTypeIds: number[]
}

export const EquipmentStatus = () => {
  const selectedPropertyId = selectedFieldAssetsStore.useSelector((s) => s.property)
  const [searchKeywords, setSearchKeywords] = useState('')
  const [insectIds, setInsectIds] = useState<number[]>([])
  const [issueTypeIds, setIssueTypeIds] = useState<number[]>([])

  const mapEquipmentStatuses = serviceCenterStore.useSelector(
    serviceCenterStore.selectors.getMapEquipmentStatuses,
  )

  const mapEquipmentTypes = serviceCenterStore.useSelector(serviceCenterStore.selectors.getMapEquipmentTypes)

  const mapEquipmentDevices = serviceCenterStore.useSelector(
    serviceCenterStore.selectors.getMapEquipmentDevices,
  )

  const filters: TFilters = useMemo(
    () => ({
      searchKeywords,
      equipmentTypes: mapEquipmentTypes,
      deviceTypes: mapEquipmentDevices,
      statuses: mapEquipmentStatuses,
      insectIds,
      issueTypeIds,
    }),
    [searchKeywords, mapEquipmentTypes, mapEquipmentDevices, mapEquipmentStatuses, insectIds, issueTypeIds],
  )

  const handleFilterChange = useCallback((filters: Partial<TFilters>) => {
    if ('searchKeywords' in filters) setSearchKeywords(filters.searchKeywords as string)

    if ('equipmentTypes' in filters) {
      serviceCenterStore.actions.setMapEquipmentTypes(filters.equipmentTypes as TEquipmentType[])

      if (!filters.equipmentTypes?.includes('trap')) setInsectIds([])
    }

    if ('deviceTypes' in filters)
      serviceCenterStore.actions.setMapEquipmentDevices(filters.deviceTypes as TNodeDeviceType[])

    if ('statuses' in filters)
      serviceCenterStore.actions.setMapEquipmentStatuses(filters.statuses as TNodeStatus[])

    if ('insectIds' in filters) setInsectIds(filters.insectIds as number[])

    if ('issueTypeIds' in filters) setIssueTypeIds(filters.issueTypeIds as number[])
  }, [])

  const nodes = serviceCenterStore.useSelector((s) =>
    serviceCenterStore.selectors.getAllPlannedAndActiveNodesForSelectedProperty(s, selectedPropertyId),
  )

  const nodeLogs = serviceCenterStore.useSelector(serviceCenterStore.selectors.getAllNodeLogs)

  const serviceIssues = serviceCenterStore.useSelector((s) =>
    serviceCenterStore.selectors.getSelectedPropertyServiceIssues(s, selectedPropertyId),
  )

  const uniqueIssueTypes = useMemo(() => {
    const uniqueIssues: Record<number, string> = {}

    Object.values(serviceIssues).forEach((serviceIssueSummaries) => {
      serviceIssueSummaries.forEach((serviceIssueSummary) => {
        uniqueIssues[serviceIssueSummary.nodeServiceIssueTypeId] =
          serviceIssueSummary.nodeServiceIssueTypeTitle
      })
    })

    return uniqueIssues
  }, [serviceIssues])

  const shapedNodes = useMemo(
    () =>
      nodes.map((node) => {
        let status

        if (isPlannedNode(node)) {
          status = TNodeStatus.PLANNED
        } else {
          const nodeId = getIdentifier(node as TActiveNode)
          const log = nodeLogs[nodeId]
          const nodeServiceIssues = serviceIssues[nodeId]

          status = getActiveNodeStatus(log)

          if (nodeServiceIssues?.length) status = TNodeStatus.ERROR
        }

        return {
          ...node,
          status,
        }
      }) as NodeWithStatus[],
    [nodes, nodeLogs, serviceIssues],
  )

  const filteredNodes = useMemo(() => {
    let formattedSearchKeywords: string[] = []

    if (searchKeywords.length) {
      formattedSearchKeywords = searchKeywords.split(',').map((k) => k.trim().toLowerCase())
    }

    let filterByEquipment = false

    // Only filter by equipment type if the user has selected some equipment, but not all
    if (mapEquipmentTypes.length > 0 && mapEquipmentTypes.length !== EQUIPMENT_TYPES.length) {
      filterByEquipment = true
    }

    let filterByDevices = false

    // Only filter by devices if the user has selected some devices, but not all
    if (mapEquipmentDevices.length > 0 && mapEquipmentDevices.length !== DEVICE_TYPES.length) {
      filterByDevices = true
    }

    const filteredNodes = shapedNodes.filter((node) => {
      if (mapEquipmentStatuses.length && !mapEquipmentStatuses.includes(node.status)) return false

      const equipmentType = getNodeEquipmentType(
        (node as TActiveNode).nodeType || (node as TActiveGateway).gatewayType,
      )

      if (filterByEquipment) {
        if (!mapEquipmentTypes.includes(equipmentType)) return false

        // If the equipment is a trap, also filter by insect type if insect types have been selected
        if (equipmentType === 'trap' && filters.insectIds.length) {
          const { targetInsectId } = (node as TActiveNode).trap || {}

          if (!targetInsectId || !filters.insectIds.includes(targetInsectId)) return false
        }
      }

      if (filterByDevices) {
        const nodeDeviceTypes = getNodeDeviceTypes((node as TActiveNode).devices)

        if (!mapEquipmentDevices.some((deviceType) => nodeDeviceTypes.includes(deviceType))) return false
      }

      if (filters.issueTypeIds.length) {
        const nodeId = getIdentifier(node as TActiveNode)
        const nodeServiceIssues = nodeId ? serviceIssues[nodeId] : []

        if (
          !nodeServiceIssues?.some((serviceIssue) =>
            filters.issueTypeIds.includes(serviceIssue.nodeServiceIssueTypeId),
          )
        ) {
          return false
        }
      }

      if (formattedSearchKeywords.length) {
        const nodeId = getIdentifier(node as TActiveNode) || ''

        if (
          !formattedSearchKeywords.some(
            (keyword) => node.name?.toLowerCase().includes(keyword) || nodeId.toLowerCase() === keyword,
          )
        ) {
          return false
        }
      }

      return true
    })

    return filteredNodes.sort((nodeA, nodeB) => {
      // Sort by status (ERROR first, then by most recent service issue)
      if (nodeA.status === TNodeStatus.ERROR && nodeB.status !== TNodeStatus.ERROR) return -1
      else if (nodeB.status === TNodeStatus.ERROR && nodeA.status !== TNodeStatus.ERROR) return 1
      else if (nodeA.status === TNodeStatus.ERROR && nodeB.status === TNodeStatus.ERROR) {
        const nodeAId = !isPlannedNode(nodeA) && getIdentifier(nodeA as TActiveNode)
        const nodeBId = !isPlannedNode(nodeB) && getIdentifier(nodeB as TActiveNode)
        const nodeAServiceIssues = nodeAId ? serviceIssues[nodeAId] : []
        const nodeBServiceIssues = nodeBId ? serviceIssues[nodeBId] : []

        if (!nodeAServiceIssues?.length && nodeBServiceIssues?.length) return 0

        const mostRecentServiceIssueForNodeA = nodeAServiceIssues?.sort(
          (a, b) => new Date(b.reportedOn).getTime() - new Date(a.reportedOn).getTime(),
        )[0]

        const mostRecentServiceIssueForNodeB = nodeBServiceIssues?.sort(
          (a, b) => new Date(b.reportedOn).getTime() - new Date(a.reportedOn).getTime(),
        )[0]

        if (!mostRecentServiceIssueForNodeA && !mostRecentServiceIssueForNodeB) return 0

        return (
          new Date(mostRecentServiceIssueForNodeB.reportedOn).getTime() -
          new Date(mostRecentServiceIssueForNodeA.reportedOn).getTime()
        )
      } else {
        return 0
      }
    })
  }, [shapedNodes, filters, serviceIssues])

  return (
    <div css={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      <Filters
        selectedPropertyId={selectedPropertyId}
        filters={filters}
        issueTypes={uniqueIssueTypes}
        onFilterChange={handleFilterChange}
        onPropertySelect={serviceCenterStore.actions.selectProperty}
      />

      <ServiceIssueSummary
        selectedIssueIds={filters.issueTypeIds}
        onIssueSelectionChange={(selectedIssueIds) => handleFilterChange({ issueTypeIds: selectedIssueIds })}
      />

      <NodeList nodes={filteredNodes} nodeLogs={nodeLogs} totalNodes={shapedNodes.length} />
    </div>
  )
}
